import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { GBP } from '../proposals/constants';
import { STATUS_LABELS } from '../../components/StatusPill';
import { ClipboardPaste } from 'lucide-react';

// Sums every "Credits used" day-total figure out of a pasted Emergent usage-history block
function sumCreditsFromPaste(text) {
    const regex = /credits\s+used[^0-9\-]*(-?[\d,]+\.?\d*)/gi;
    let match, total = 0, count = 0;
    while ((match = regex.exec(text)) !== null) {
        const num = parseFloat(match[1].replace(/,/g, ''));
        if (!isNaN(num)) { total += num; count += 1; }
    }
    return { total: Math.round(total * 100) / 100, count };
}

/**
 * Shared "Log cost" dialog — used from the Profitability page, the monthly
 * quick-entry grid, and the Client → Profitability tab.
 *
 * Pass `clientId` / `period` to lock those fields (grid-cell / client-tab
 * usage); omit them to let the user pick (top-level "Log cost" button).
 * Pass `entry` to edit an existing cost entry — reopens in its original mode.
 */
export default function CostEntryDialog({
    open, onOpenChange, clients = [], clientId, period, entry, defaultRate, onSaved,
}) {
    const isEdit = !!entry;
    const [mode, setMode] = useState('credits');
    const [selClientId, setSelClientId] = useState(clientId || '');
    const [selPeriod, setSelPeriod] = useState(period || '');
    const [credits, setCredits] = useState('');
    const [rate, setRate] = useState('');
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [pasteOpen, setPasteOpen] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [pasteResult, setPasteResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setPasteOpen(false);
        setPasteText('');
        setPasteResult(null);
        if (entry) {
            setMode(entry.entry_mode || 'credits');
            setSelClientId(entry.client_id);
            setSelPeriod(entry.period);
            setCredits(entry.credits_used ?? '');
            setRate(entry.rate_applied ?? '');
            setAmount(entry.amount ?? '');
            setNote(entry.note || '');
        } else {
            setMode('credits');
            setSelClientId(clientId || '');
            setSelPeriod(period || '');
            setCredits('');
            setRate(defaultRate ?? '');
            setAmount('');
            setNote('');
        }
    }, [open, entry, clientId, period, defaultRate]);

    const computedAmount = mode === 'credits'
        ? (parseFloat(credits) || 0) * (parseFloat(rate) || 0)
        : (parseFloat(amount) || 0);

    const applyPasteSum = () => {
        const { total, count } = sumCreditsFromPaste(pasteText);
        if (!count) {
            setPasteResult({ error: true });
            return;
        }
        setCredits(String(total));
        setPasteResult({ total, count });
    };

    const save = async () => {
        if (!selClientId) return toast.error('Select a client');
        if (!selPeriod) return toast.error('Select a month');
        if (mode === 'credits') {
            if (!credits || !rate) return toast.error('Enter credits used and the £/credit rate');
        } else if (!amount) {
            return toast.error('Enter a £ amount');
        }
        setSaving(true);
        try {
            const payload = mode === 'credits'
                ? { client_id: selClientId, period: selPeriod, credits_used: parseFloat(credits), rate_applied: parseFloat(rate), note: note || null }
                : { client_id: selClientId, period: selPeriod, amount: parseFloat(amount), note: note || null };
            let r;
            if (isEdit) {
                const upd = mode === 'credits'
                    ? { credits_used: parseFloat(credits), rate_applied: parseFloat(rate), note: note || null }
                    : { amount: parseFloat(amount), note: note || null };
                r = await api.patch(`/profitability/cost-entries/${entry.id}`, upd);
            } else {
                r = await api.post('/profitability/cost-entries', payload);
            }
            toast.success(isEdit ? 'Cost entry updated' : 'Cost entry logged');
            onSaved && onSaved(r.data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="cost-entry-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit cost entry' : 'Log a cost'}</DialogTitle>
                </DialogHeader>

                <div className="space-y-3.5">
                    {!clientId && (
                        <div>
                            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Client</Label>
                            <Select value={selClientId} onValueChange={setSelClientId} disabled={isEdit}>
                                <SelectTrigger className="mt-1.5 h-9" data-testid="cost-entry-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                                <SelectContent>{clients.map(c => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.company_name}{c.status !== 'active' ? ` — ${STATUS_LABELS[c.status] || c.status}` : ''}
                                    </SelectItem>
                                ))}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {!period && (
                        <div>
                            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Month</Label>
                            <Input type="month" value={selPeriod} onChange={e => setSelPeriod(e.target.value)}
                                   disabled={isEdit} className="mt-1.5 h-9" data-testid="cost-entry-period-input" />
                        </div>
                    )}

                    <div className="flex gap-1.5 p-1 rounded-lg bg-[color:var(--bm-bg-mute)] w-fit">
                        {['credits', 'amount'].map(m => (
                            <button key={m} type="button" onClick={() => setMode(m)}
                                    data-testid={`cost-entry-mode-${m}`}
                                    className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium transition ${mode === m ? 'bg-white shadow-sm text-[color:var(--bm-text)]' : 'text-[color:var(--bm-text-muted)]'}`}>
                                {m === 'credits' ? 'Credits' : 'Direct £'}
                            </button>
                        ))}
                    </div>

                    {mode === 'credits' ? (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Credits used</Label>
                                    <Input type="number" value={credits} onChange={e => setCredits(e.target.value)}
                                           className="mt-1.5 h-9" placeholder="1000" data-testid="cost-entry-credits-input" />
                                </div>
                                <div>
                                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">£ per credit</Label>
                                    <Input type="number" step="0.001" value={rate} onChange={e => setRate(e.target.value)}
                                           className="mt-1.5 h-9" placeholder="0.15" data-testid="cost-entry-rate-input" />
                                </div>
                            </div>
                            <div className="text-[12.5px] text-[color:var(--bm-text-muted)]" data-testid="cost-entry-computed-amount">
                                = <span className="font-semibold text-[color:var(--bm-text)]">{GBP(computedAmount)}</span>
                            </div>

                            <button type="button" onClick={() => setPasteOpen(v => !v)}
                                    className="flex items-center gap-1.5 text-[11.5px] font-medium text-[color:var(--bm-primary)] hover:underline"
                                    data-testid="cost-entry-paste-toggle">
                                <ClipboardPaste size={12} /> {pasteOpen ? 'Hide' : 'Paste usage history to auto-sum'}
                            </button>

                            {pasteOpen && (
                                <div className="rounded-lg border border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] p-3 space-y-2">
                                    <Textarea value={pasteText} onChange={e => { setPasteText(e.target.value); setPasteResult(null); }}
                                              rows={4} placeholder="Paste the Emergent 'Usage history' block here — every 'Credits used' figure will be added up."
                                              className="bg-white text-[11.5px]" data-testid="cost-entry-paste-textarea" />
                                    <div className="flex items-center justify-between gap-3">
                                        <Button type="button" size="sm" variant="outline" className="h-7 text-[11.5px]"
                                                onClick={applyPasteSum} data-testid="cost-entry-paste-calculate-btn">
                                            Calculate &amp; fill
                                        </Button>
                                        {pasteResult && !pasteResult.error && (
                                            <span className="text-[11.5px] text-emerald-700 font-medium" data-testid="cost-entry-paste-result">
                                                Summed {pasteResult.count} {pasteResult.count === 1 ? 'entry' : 'entries'} → {pasteResult.total.toLocaleString()} credits
                                            </span>
                                        )}
                                        {pasteResult && pasteResult.error && (
                                            <span className="text-[11.5px] text-red-600 font-medium" data-testid="cost-entry-paste-error">
                                                No "Credits used" figures found in that text
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">£ amount</Label>
                            <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                                   className="mt-1.5 h-9" placeholder="150.00" data-testid="cost-entry-amount-input" />
                        </div>
                    )}

                    <div>
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Note (optional)</Label>
                        <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                                  className="mt-1.5" data-testid="cost-entry-note-input" />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cost-entry-cancel-btn">Cancel</Button>
                    <Button onClick={save} disabled={saving} style={{ background: 'var(--bm-primary)' }} data-testid="cost-entry-save-btn">
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
